import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { request } from "./request";

dayjs.extend(utc);

const VN_OFFSET_MINUTES = 7 * 60;

// Định dạng ngày sinh gửi cho Flow ZNS theo ngày (mẫu Flow: "01/08/2020" = ngày/tháng/năm)
export const BIRTHDAY_DAY_FORMAT = "DD/MM/YYYY";

export const SENT_FIELD = {
	thang: "zns_sinh_nhat_thang",
	ngay: "zns_sinh_nhat_ngay",
};

export const WEBHOOK_FIELD = {
	thang: "link_webhook_sinh_nhat_thang",
	ngay: "link_webhook_sinh_nhat_ngay",
};

const pad = (n) => String(n).padStart(2, "0");

export const normalizePhone = (raw) => {
	let phone = String(raw ?? "").replace(/[\s.\-()]/g, "");
	if (phone.startsWith("+84")) {
		phone = "0" + phone.slice(3);
	} else if (phone.startsWith("84") && phone.length === 11) {
		phone = "0" + phone.slice(2);
	}
	return phone;
};

const VN_MOBILE_REGEX = /^0(3[2-9]|5[2689]|7[06-9]|8[1-9]|9\d)\d{7}$/;

export const isValidVnMobile = (raw) => VN_MOBILE_REGEX.test(normalizePhone(raw));

// Ngày sinh quy về giờ Việt Nam (không phụ thuộc múi giờ của máy đang mở CRM)
export const getBirthdayVN = (ngaySinh) => {
	if (!ngaySinh) return null;
	const date = dayjs(ngaySinh);
	return date.isValid() ? date.utcOffset(VN_OFFSET_MINUTES) : null;
};

export const getCustomerName = (customer) =>
	`${customer?.ho_ten_dem ?? ""} ${customer?.ten_kh ?? ""}`.trim();

// Kỳ gửi để chống gửi trùng: tháng "YYYY-MM", ngày "YYYY-MM-DD" của năm đang xem.
// Sinh ngày 29/2 ở năm không nhuận được tính vào 28/2.
export const getPeriodKeys = (birthday, viewYear) => {
	const monthIndex = birthday.month();
	const daysInMonth = dayjs(`${viewYear}-${pad(monthIndex + 1)}-01`).daysInMonth();
	const day = Math.min(birthday.date(), daysInMonth);
	const month = `${viewYear}-${pad(monthIndex + 1)}`;
	return { month, day: `${month}-${pad(day)}` };
};

export const buildBirthdayPayload = (type, customer, birthday) => {
	const customerName = getCustomerName(customer);
	const phone = normalizePhone(customer?.dien_thoai);
	if (type === "thang") {
		return {
			customer_id: customer?.ma_kh,
			customer_name: customerName,
			phone,
			month: birthday.month() + 1,
		};
	}
	// Flow theo ngày chỉ nhận đúng 3 field này, thừa field sẽ bị báo sai định dạng JSON
	return {
		birthday_date: birthday.format(BIRTHDAY_DAY_FORMAT),
		customer_name: customerName,
		phone,
	};
};

// Gọi webhook gửi ZNS, thành công thì ghi cờ vào customer.exfields để không gửi trùng.
// Lỗi gọi webhook sẽ throw; lỗi chỉ ở bước ghi cờ trả về marked: false.
export const sendBirthdayZns = async ({
	type,
	customer,
	birthday,
	periodKey,
	linkWebhook,
	idApp,
	token,
}) => {
	await axios.post(linkWebhook, buildBirthdayPayload(type, customer, birthday), {
		headers: { "Content-Type": "application/json" },
	});

	const headers = {
		"X-Access-Token": token,
		"Content-Type": "application/json",
	};
	// Lấy lại hồ sơ mới nhất rồi mới merge exfields, tránh ghi đè các mảng lớn
	// (trich_xuat_bn, hinh_anh...) bằng dữ liệu cũ nếu nhân viên đang sửa hồ sơ.
	let latest = customer;
	try {
		const res = await request.get(`api/${idApp}/customer/${customer._id}`, {
			headers,
		});
		if (res?.data && !Array.isArray(res.data)) latest = res.data;
	} catch (e) {
		console.error("Khong lay lai duoc ho so khach", customer?._id, e);
	}

	const field = SENT_FIELD[type];
	const exfields = {
		...latest?.exfields,
		[field]: periodKey,
		[`${field}_luc`]: new Date().toISOString(),
	};
	try {
		await request.put(
			`api/${idApp}/customer/${customer._id}`,
			{ _id: customer._id, exfields },
			{ headers },
		);
		return { marked: true, exfields };
	} catch (e) {
		console.error("Khong ghi duoc co da gui", customer?._id, e);
		return { marked: false, exfields };
	}
};

// Doc lai ho so moi nhat, ap dung mutate len exfields roi PUT chi exfields.
const patchCustomerExfields = async ({ customer, idApp, token, mutate }) => {
	const headers = {
		"X-Access-Token": token,
		"Content-Type": "application/json",
	};
	const res = await request.get(`api/${idApp}/customer/${customer._id}`, {
		headers,
	});
	const latest = res?.data && !Array.isArray(res.data) ? res.data : customer;
	const exfields = mutate({ ...latest?.exfields });
	await request.put(
		`api/${idApp}/customer/${customer._id}`,
		{ _id: customer._id, exfields },
		{ headers },
	);
	return exfields;
};

// Gui tu dong: ghi co "da gui" TRUOC khi goi webhook de khong the gui trung,
// goi webhook loi thi go co ra de lan quet sau thu lai.
// Tra ve: "sent" | "skipped" (da co co) | "failed"
export const autoSendBirthdayZns = async ({
	type,
	customer,
	birthday,
	periodKey,
	linkWebhook,
	idApp,
	token,
}) => {
	const field = SENT_FIELD[type];
	let alreadySent = false;
	try {
		await patchCustomerExfields({
			customer,
			idApp,
			token,
			mutate: (exfields) => {
				if (exfields[field] === periodKey) {
					alreadySent = true;
					return exfields;
				}
				return {
					...exfields,
					[field]: periodKey,
					[`${field}_luc`]: new Date().toISOString(),
				};
			},
		});
	} catch (e) {
		console.error("Khong ghi duoc co truoc khi gui", customer?._id, e);
		return "failed";
	}
	if (alreadySent) return "skipped";

	try {
		await axios.post(
			linkWebhook,
			buildBirthdayPayload(type, customer, birthday),
			{ headers: { "Content-Type": "application/json" } },
		);
		return "sent";
	} catch (e) {
		console.error("Gui ZNS sinh nhat loi", customer?._id, e);
		try {
			await patchCustomerExfields({
				customer,
				idApp,
				token,
				mutate: (exfields) => {
					const next = { ...exfields };
					if (next[field] === periodKey) {
						delete next[field];
						delete next[`${field}_luc`];
					}
					return next;
				},
			});
		} catch (e2) {
			console.error("Khong go duoc co sau khi gui loi", customer?._id, e2);
		}
		return "failed";
	}
};
